import React, { Component } from 'react';
import styled from '@emotion/styled';
import { colors, respondFrom, breakpoints, css, mixins, respondTo } from '@styles';
import ShoppingSummaryButton from '@components/configurator/shoppingCart/ShoppingSummaryButton';
import ShoppingSummaryButtonReturn from '@components/configurator/shoppingCart/ShoppingSummaryButtonReturn';
import RadioButton from '@components/common/RadioButton';
import BorderLine from '@components/common/BorderLine';
import { ConfiguratorBasket, ConfiguratorState } from '@store/types';
import { ConfiguratorActions } from '@store/actions';
import { FormData, LeasingInfo, Product } from '@store/content/types';
import { LocationDependent } from '@content/types/general';
import { ConfiguratorSettings } from '@content/types/configuratorSettings';
import { Funding } from '@content/types/funding';
import ShoppingSummaryDiscounts from './ShoppingSummaryDiscounts';
import TooltipIcon from '@components/common/TooltipIcon';
import ShoppingSummaryPrices from './ShoppingSummaryPrices';
import { PricesSummaryType } from '@content/types/prices';
import * as utils from '@utils';
import { Agreement } from '@content/types/agreement';

interface ShoppingSummaryProps extends LocationDependent {
  active: boolean;
  basket: ConfiguratorBasket;
  activeStep: string;
  configuratorActions: typeof ConfiguratorActions;
  currentProductIndex: number;
  formData: FormData;
  onPayButtonClick: Function;
  paymentType: string;
  leasingInfo: LeasingInfo;
  pageSettings: ConfiguratorSettings;
  langcode: string;
  urls: Array<string>;
  isOrderLoading: boolean;
  configurator: ConfiguratorState;
  allFundings: Funding[];
  currentFundings: Funding[];
  hide: boolean;
  prices: PricesSummaryType;
  products: Product[];
  onAfterRemoveDiscount: Function;
  agreements: Agreement[];
}

interface StyledWrapper {
  hide: boolean;
}

const Wrapper = styled.div<StyledWrapper>`
  background-color: ${colors.text.default};
  /* height: 156px; */
  padding: 15px 15px 20px;
  ${mixins.fontSmoothing}
  max-height: 100%;
  transition: max-height 0.25s ease-in;
  
  ${respondTo(
    breakpoints.lg,
    css`
       @media screen and (orientation:landscape) {
        max-height: 200px;
        overflow-y: auto;
      }
    `
  )}

  z-index: 2;

  &.bigger-area {
    /* height: 205px; */
  }

  ${respondFrom(
    breakpoints.lg,
    css`
      padding: 20px 15px 30px 15px;
      /* height: 345px; */

      &.bigger-area {
        /* height: 385px; */
      }
    `
  )}

  .border-line {
  }

  ${({ hide }) => hide && css`
    ${respondTo(
      breakpoints.lg,
      css`
        max-height: 0;
        padding: 0;
        transition: max-height 0.15s ease-out, padding 0s 0.15s;
        overflow: hidden;
      `
    )}
  `};
`;

const StyledPaymentTypeHeader = styled.h3`
  font-size: 14px;
  font-weight: bold;
  color: ${colors.white};
`;

const RadioButtons = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

class ShoppingSummary extends Component<ShoppingSummaryProps> {
  onChangeRadio = name => {
    this.props.configuratorActions.setPaymentType(name);
  };

  render() {
    const {
      active,
      basket,
      currentProductIndex,
      activeStep,
      formData,
      onPayButtonClick,
      paymentType,
      leasingInfo,
      location,
      pageSettings,
      langcode,
      urls,
      isOrderLoading,
      configurator,
      allFundings,
      configuratorActions,
      currentFundings,
      hide,
      prices,
      products,
      onAfterRemoveDiscount,
      agreements
    } = this.props;

    const checkProductTypes = (basket): string => {
      if (!basket.products.length) {
        return '';
      }

      const firstProductType = basket.products[0].productType;
      const allSameType = basket.products.every(product => product.productType === firstProductType);

      return allSameType ? firstProductType : '';
    };

    return (
      <Wrapper className={activeStep === 'krok-1' ? '' : 'bigger-area'} hide={hide}>
        <StyledPaymentTypeHeader>
          {pageSettings.field_proste_pola ? pageSettings.field_proste_pola[7] : ''}
          <TooltipIcon
            data-html={true}
            data-tip={pageSettings.field_pola_wysywig && pageSettings.field_pola_wysywig[11]
              ? pageSettings.field_pola_wysywig[11].value
              : ''}
            data-for="tooltipPaymentType"
          />
        </StyledPaymentTypeHeader>
        <RadioButtons>
          <RadioButton
            idInput="lease"
            nameInput="payment"
            nameButton={pageSettings.field_proste_pola ? pageSettings.field_proste_pola[141] : ''}
            onChangeRadio={this.onChangeRadio}
            radioChecked={this.props.paymentType}
            inActive={products && !products.find(product => utils.productHasAvailablePaymentType(product, 'lease'))}
            size='large'
          />
          <RadioButton
            idInput="cash"
            nameInput="payment"
            nameButton={pageSettings.field_proste_pola ? pageSettings.field_proste_pola[8] : ''}
            onChangeRadio={this.onChangeRadio}
            radioChecked={this.props.paymentType}
            inActive={products && !products.find(product => utils.productHasAvailablePaymentType(product, 'cash'))}
            size='large'
          />
          <RadioButton
            idInput="leasing"
            nameInput="payment"
            nameButton={pageSettings.field_proste_pola ? pageSettings.field_proste_pola[9] : ''}
            onChangeRadio={this.onChangeRadio}
            radioChecked={this.props.paymentType}
            inActive={products && !products.find(product => utils.productHasAvailablePaymentType(product, 'leasing')) /*priceNet >= leasingInfo.maxPrice ? true : false*/}
            noMargin={true}
            size='large'
          />
        </RadioButtons>
        <BorderLine color={colors.summaryBorder} />
        <ShoppingSummaryDiscounts
          configurator={configurator}
          configuratorActions={configuratorActions}
          allFundings={allFundings}
          currentFundings={currentFundings}
          basket={basket}
          pageSettings={pageSettings}
          paymentType={paymentType}
          onAfterRemoveDiscount={onAfterRemoveDiscount}
        />
         <ShoppingSummaryPrices
           pageSettings={pageSettings}
           leasingInfo={leasingInfo}
           prices={prices}
           paymentType={paymentType}
           productsTypes={checkProductTypes(basket)}
        />
        <ShoppingSummaryButton
          hideOnMobile={true}
          active={active}
          basket={basket}
          activeStep={activeStep}
          currentProductIndex={currentProductIndex}
          configuratorActions={this.props.configuratorActions}
          formData={formData}
          onPayButtonClick={onPayButtonClick}
          width="full"
          location={location}
          pageSettings={pageSettings}
          paymentType={paymentType}
          langcode={langcode}
          urls={urls}
          isOrderLoading={isOrderLoading}
          agreements={agreements}
        />
        <ShoppingSummaryButtonReturn
          hideOnMobile={true}
          basket={basket}
          activeStep={activeStep}
          currentProductIndex={currentProductIndex}
          configuratorActions={this.props.configuratorActions}
          location={location}
          pageSettings={pageSettings}
          langcode={langcode}
          urls={urls}
        />
      </Wrapper>
    );
  }
}

export default ShoppingSummary;
